// ---------------------------------------------------------------------------
// AI Conversation Threads + Tool Executions (Council 9 spec)
// ---------------------------------------------------------------------------

import { pgTable, uuid, varchar, text, integer, boolean, jsonb, timestamp, index } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

/** AI conversation threads — multi-turn AI assistant conversations */
export const aiConversationThreads = pgTable(
  "ai_conversation_threads",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    userId: uuid("user_id").notNull(),
    agentId: uuid("agent_id"),
    title: varchar("title", { length: 500 }),
    messages: jsonb("messages").$type<Array<{
      role: "user" | "assistant" | "system";
      content: string;
      toolCalls?: Array<{ name: string; arguments: Record<string, unknown>; result?: unknown }>;
      createdAt: string;
    }>>().notNull().default([]),
    contextEntityType: varchar("context_entity_type", { length: 50 }),
    contextEntityId: uuid("context_entity_id"),
    tokenCount: integer("token_count").notNull().default(0),
    lastMessageAt: timestamp("last_message_at", tz).notNull().defaultNow(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_ai_thread_user").on(table.tenantId, table.userId, table.lastMessageAt),
    index("idx_ai_thread_entity").on(table.tenantId, table.contextEntityType, table.contextEntityId),
  ],
);

/** AI tool executions — every tool call by the AI, with EU AI Act confirmation tracking */
export const aiToolExecutions = pgTable(
  "ai_tool_executions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    threadId: uuid("thread_id"),
    agentId: uuid("agent_id"),
    toolName: varchar("tool_name", { length: 200 }).notNull(),
    arguments: jsonb("arguments").$type<Record<string, unknown>>().notNull().default({}),
    result: jsonb("result").$type<Record<string, unknown>>(),
    status: varchar("status", { length: 20 }).notNull(), // pending | completed | failed | rejected
    durationMs: integer("duration_ms"),
    errorMessage: text("error_message"),
    requiredHumanConfirmation: boolean("required_human_confirmation").notNull().default(false),
    confirmedBy: uuid("confirmed_by"),
    entityType: varchar("entity_type", { length: 50 }),
    entityId: uuid("entity_id"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_ai_exec_tenant_created").on(table.tenantId, table.createdAt),
    index("idx_ai_exec_thread").on(table.threadId),
    index("idx_ai_exec_tool").on(table.tenantId, table.toolName),
  ],
);
